// Table of Contents extractor and renderer for TipTap editors.
// Supports both single-doc and sectioned document modes.

export function extractHeadings(editor) {
  if (!editor || editor.isDestroyed) return []
  const headings = []

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "heading") {
      headings.push({
        level: node.attrs.level,
        text: node.textContent,
        pos: pos,
      })
    }
  })

  return headings
}

export function extractSectionedHeadings(sectionControllers) {
  if (!sectionControllers) return []
  const sections = []

  sectionControllers.forEach((ctrl, sectionId) => {
    if (!ctrl.editor || ctrl.editor.isDestroyed || !ctrl.element?.isConnected) return
    const label = ctrl.element?.dataset?.sectionLabel || `Section ${(ctrl.positionValue || 0) + 1}`
    const headings = ctrl.editor ? extractHeadings(ctrl.editor) : []

    sections.push({
      sectionId,
      sectionLabel: label,
      editorIndex: ctrl.positionValue || 0,
      headings,
      editor: ctrl.editor,
    })
  })

  sections.sort((a, b) => a.editorIndex - b.editorIndex)
  return sections
}

export function renderTocList(entries, onClick) {
  const list = document.createElement("div")
  list.className = "toc-list"

  entries.forEach((entry, index) => {
    const item = document.createElement("button")
    item.type = "button"
    item.tocEntry = entry
    item.className = `toc-entry toc-level-${entry.level}`
    item.dataset.pos = entry.pos
    item.dataset.index = index
    if (entry.editorRef) item.dataset.editorRef = entry.editorRef
    item.style.paddingLeft = `${12 * entry.level}px`
    item.innerHTML = `<span class="toc-entry-text">${escapeHtml(entry.text || "Untitled")}</span>`
    item.addEventListener("click", () => onClick(entry))
    list.appendChild(item)
  })

  return list
}

export function renderSectionedTocList(sections, onClick) {
  const list = document.createElement("div")
  list.className = "toc-list"

  sections.forEach(section => {
    // Section header
    const header = document.createElement("div")
    header.className = "toc-section-label"
    header.innerHTML = `<span class="fas fa-file-alt mr-2 opacity-4"></span>${escapeHtml(section.sectionLabel)}`
    list.appendChild(header)

    // Headings within section
    if (section.headings.length === 0) {
      const empty = document.createElement("div")
      empty.className = "toc-entry toc-empty"
      empty.style.paddingLeft = "24px"
      empty.innerHTML = `<span class="text-muted font-size-12">No headings</span>`
      list.appendChild(empty)
    } else {
      const headings = section.headings.map(heading => ({ ...heading, editor: section.editor }))
      const entries = renderTocList(headings, onClick)
      Array.from(entries.children).forEach(item => {
        item.style.paddingLeft = `${12 + 12 * item.tocEntry.level}px`
        list.appendChild(item)
      })
    }
  })

  return list
}

export function scrollToHeading(editor, pos) {
  if (!editor || editor.isDestroyed) return
  editor.commands.setTextSelection(pos + 1)
  // Avoid a second, delayed caret scroll undoing the toolbar clearance below.
  editor.commands.focus(null, { scrollIntoView: false })
  const heading = editor.view.nodeDOM(pos)
  if (!heading?.getBoundingClientRect) return
  const content = editor.view.dom.closest(".editor-content")
  const toolbar = content?.querySelector(".tiptap-fixed-toolbar")
  let scrollOwner = content
  while (scrollOwner && scrollOwner !== document.body && scrollOwner !== document.documentElement) {
    if (/^(auto|scroll|hidden)$/.test(getComputedStyle(scrollOwner).overflowY) && scrollOwner.scrollHeight > scrollOwner.clientHeight) break
    scrollOwner = scrollOwner.parentElement
  }
  if (scrollOwner === document.body || scrollOwner === document.documentElement) scrollOwner = null
  const visibleTop = () => Math.max(0, scrollOwner?.getBoundingClientRect().top || 0, toolbar?.getBoundingClientRect().bottom || 0) + 8
  // Move only the document's scroll owner. DOM scrollIntoView would also move
  // the outer desktop viewport and hide the editor header.
  const scroll = adjustment => {
    if (scrollOwner) scrollOwner.scrollTop += adjustment
    else window.scrollBy(0, adjustment)
  }
  scroll(Math.floor(heading.getBoundingClientRect().top - visibleTop()))
  // A page scroll can make the toolbar sticky; clear its new position too.
  const clipped = Math.floor(heading.getBoundingClientRect().top - visibleTop())
  if (clipped < 0) scroll(clipped)
}

function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}
